// pages/AlertPage.js - 告警管理
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Table, Tag, Button, Modal, Form, Input, Select, Row, Col, message } from 'antd';
import { AlertOutlined, WarningOutlined, ClockCircleOutlined, CheckCircleOutlined, DeleteOutlined } from '@ant-design/icons';
import { api } from '../services/api';

const cardStyle = {
  background: 'linear-gradient(135deg, #0d1f44 0%, #132c5e 100%)',
  border: '1px solid rgba(0, 180, 216, 0.3)',
  borderRadius: 8,
  boxShadow: '0 0 20px rgba(0, 180, 216, 0.1)',
};
const COLORS = { primary: '#00d4ff', solar: '#f4c542', text: '#fff', muted: 'rgba(255,255,255,0.6)' };

// severity: 4=high, 3=warning, 2=normal, 1=low
const levelConfig = (t) => ({
  4: { color: 'red', label: t('alert.levelHigh'), icon: <WarningOutlined /> },
  3: { color: 'orange', label: t('alert.levelWarning'), icon: <AlertOutlined /> },
  2: { color: 'blue', label: t('alert.levelNormal'), icon: <ClockCircleOutlined /> },
  1: { color: 'default', label: t('alert.levelLow'), icon: <ClockCircleOutlined /> },
});
const statusConfig = (t) => ({
  0: { color: 'orange', label: t('alert.statusPending') },
  1: { color: 'green', label: t('alert.statusProcessed') },
  2: { color: 'default', label: t('alert.statusIgnored') },
});

const AlertPage = () => {
  const { t } = useTranslation();
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, processed: 0, severity_high: 0, severity_warning: 0, severity_normal: 0, severity_low: 0, today_count: 0 });
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState({ severity: '', status: '', page: 1, pageSize: 20 });
  const [detailVisible, setDetailVisible] = useState(false);
  const [selected, setSelected] = useState(null);
  const [handleForm] = Form.useForm();

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: filter.page, pageSize: filter.pageSize };
      if (filter.severity) params.severity = filter.severity;
      if (filter.status !== '') params.status = filter.status;
      const json = await api.getAlarmRecordList(params);
      if (json.success) {
        setAlerts(json.data || []);
        setTotal(json.total || 0);
      }
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [filter]);

  const fetchStats = useCallback(async () => {
    try {
      const json = await api.getAlarmRecordStats();
      if (json.success) {
        const d = json.data || {};
        setStats({
          total: d.total || 0,
          pending: d.pending || 0,
          processed: d.processed || 0,
          severity_high: d.severity_high || 0,
          severity_warning: d.severity_warning || 0,
          severity_normal: d.severity_normal || 0,
          severity_low: d.severity_low || 0,
          today_count: d.today_count || 0,
        });
      }
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  const handleProcess = async (record) => {
    setSelected(record);
    handleForm.setFieldsValue({ handler_note: '' });
    Modal.confirm({
      title: t('alert.processAlert'),
      icon: null,
      className: 'dark-confirm-modal',
      okText: t('alert.confirmProcess'),
      cancelText: t('common.cancel'),
      content: (
        <Form form={handleForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="handler_note" label={t('alert.note')}>
            <Input.TextArea rows={3} placeholder={t('alert.notePlaceholder')} />
          </Form.Item>
        </Form>
      ),
      onOk: async () => {
        try {
          const values = await handleForm.validateFields();
          const json = await api.handleAlarmRecord(record.id, { status: 1, handler_note: values.handler_note || '' });
          if (json.success) {
            message.success(t('alert.processSuccess'));
            fetchAlerts();
            fetchStats();
          } else { message.error(t('alert.processFailed')); }
        } catch (err) { message.error(t('alert.processFailed')); }
      },
    });
  };

  const handleIgnore = async (id) => {
    Modal.confirm({
      title: t('alert.ignoreAlert'),
      content: t('alert.confirmIgnore'),
      className: 'dark-confirm-modal',
      okText: t('common.confirm'), cancelText: t('common.cancel'),
      onOk: async () => {
        const json = await api.handleAlarmRecord(id, { status: 2 });
        if (json.success) { message.success(t('alert.ignored')); fetchAlerts(); fetchStats(); }
        else message.error(t('alert.operationFailed'));
      },
    });
  };

  const handleDelete = async (id) => {
    Modal.confirm({
      title: t('alert.deleteAlert'),
      content: t('alert.deleteConfirm'),
      className: 'dark-confirm-modal',
      okText: t('common.confirm'), cancelText: t('common.cancel'),
      onOk: async () => {
        const json = await api.deleteAlarmRecord(id);
        if (json.success) { message.success(t('alert.deleteSuccess')); fetchAlerts(); fetchStats(); }
        else message.error(t('alert.deleteFailed'));
      },
    });
  };

  const columns = [
    {
      title: t('alert.level'), dataIndex: 'severity', key: 'severity', width: 90,
      render: v => {
        const colorMap = { 4: 'red', 3: 'orange', 2: 'blue', 1: 'default' };
        return <Tag color={colorMap[v] || 'default'}>{v}</Tag>;
      }
    },
    {
      title: t('alert.device'), dataIndex: 'device_name', key: 'device_name',
      render: val => <span style={{ color: COLORS.muted }}>{val || '-'}</span>,
    },
    {
      title: t('cloud.stationName'), dataIndex: 'station_name', key: 'station_name',
      render: val => <span style={{ color: COLORS.muted }}>{val || '-'}</span>,
    },
    {
      title: t('alert.status'), dataIndex: 'status', key: 'status', width: 90,
      render: v => <Tag color={statusConfig(t)[v]?.color}>{statusConfig(t)[v]?.label || v}</Tag>,
    },
    {
      title: t('alert.time'), dataIndex: 'raise_time', key: 'raise_time',
      render: val => <span style={{ color: COLORS.muted, fontSize: 12 }}>{val ? String(val).substring(0, 16) : '-'}</span>,
    },
    {
      title: t('alert.action'), key: 'action', width: 200,
      render: (_, r) => (
        <>
          {r.status === 0 && (
            <Button type="link" style={{ color: '#52c41a' }} onClick={() => handleProcess(r)}>{t('alert.process')}</Button>
          )}
          {r.status === 0 && (
            <Button type="link" style={{ color: COLORS.muted }} onClick={() => handleIgnore(r.id)}>{t('alert.ignore')}</Button>
          )}
          <Button type="link" style={{ color: '#ff4d4f' }} onClick={() => handleDelete(r.id)}>{t('alert.delete')}</Button>
        </>
      ),
    },
  ];

  return (
    <div style={{ minHeight: '100%' }}>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        {[
          { label: t('alert.pendingAlert'), value: stats.pending, color: '#faad14' },
          { label: t('alert.highAlert'), value: stats.severity_high, color: '#ff4d4f' },
          { label: t('alert.todayAlert'), value: stats.today_count, color: COLORS.primary },
          { label: t('alert.processed'), value: stats.processed, color: '#52c41a' },
        ].map(item => (
          <Col xs={24} sm={12} lg={6} key={item.label}>
            <Card variant="borderless" style={cardStyle} styles={{ body: { padding: '20px' } }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ width: 48, height: 48, borderRadius: 10, background: `${item.color}22`, display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1px solid ${item.color}44` }}>
                  <AlertOutlined style={{ fontSize: 22, color: item.color }} />
                </div>
                <div>
                  <div style={{ fontSize: 12, color: COLORS.muted }}>{item.label}</div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: item.color }}>{item.value}</div>
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <Card variant="borderless" style={cardStyle} styles={{ body: { padding: 20 } }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.text, letterSpacing: 2, paddingLeft: 12, borderLeft: '3px solid #00d4ff' }}>{t('alert.title')}</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Select placeholder={t('alert.filterLevel')} allowClear style={{ width: 100 }}
              onChange={v => setFilter(f => ({ ...f, severity: v || '', page: 1 }))}
              options={[
                { value: 4, label: t('alert.levelHigh') },
                { value: 3, label: t('alert.levelWarning') },
                { value: 2, label: t('alert.levelNormal') },
                { value: 1, label: t('alert.levelLow') },
              ]}
            />
            <Select placeholder={t('alert.filterStatus')} allowClear style={{ width: 100 }}
              onChange={v => setFilter(f => ({ ...f, status: v ?? '', page: 1 }))}
              options={[
                { value: 0, label: t('alert.statusPending') },
                { value: 1, label: t('alert.statusProcessed') },
                { value: 2, label: t('alert.statusIgnored') },
              ]}
            />
          </div>
        </div>
        <Table
          columns={columns}
          dataSource={alerts}
          loading={loading}
          rowKey="id"
          pagination={{
            current: filter.page,
            pageSize: filter.pageSize,
            total,
            onChange: (page, ps) => setFilter(f => ({ ...f, page, pageSize: ps })),
          }}
          style={{ background: 'transparent' }}
        />
      </Card>
    </div>
  );
};

export default AlertPage;
